import React, { Suspense, useCallback, useEffect, Fragment, lazy } from "react";
import { IGif, IUser } from "@/common/services/types";
import { mainBus, metricsBus, EventSource } from "@/common/buses";
import { onOpenMoreMenu } from "@/components/popup-dispatcher/PopupDispatcher";
import { useVisitorInfo } from "@/common/hooks/useVisitorInfo";
import { LikeSoundOverlay } from "../LikeSoundOverlay";
import { Player } from "../Player";
import { ImageGif } from "../ImageGif";
import { usePlayerSound } from "./hooks/usePlayerSound";
import { GifType } from "./constants";
import { useAnalyticsStore } from "@/common/store/analyticsStore";
import { Sidebar } from "../Sidebar";
import { MetaInfo } from "../MetaInfo";
import { useGalleryGif } from "./hooks/useGalleryGif";
import { useLikeActions } from "./hooks/useLikeActions";
import "./GifPreview.scss";

const GalleryGif = lazy(() => import("../GalleryGif"));

interface IGifPreviewProps {
  user: IUser | undefined;
  rel?: string;
  position: number;
  wrapRef: React.RefObject<HTMLDivElement>;
  gif: IGif;
  isVisible: boolean;
  playCount: number;
  incrementPlayCount: () => void;
  hideGif: (id: string) => void;
  sendGifView: (gif: IGif) => void;
}

export const GifPreview: React.FC<IGifPreviewProps> = (props) => {
  const {
    wrapRef,
    gif,
    isVisible,
    position,
    hideGif,
    sendGifView,
    playCount,
    incrementPlayCount
  } = props;

  const {
    currentGalleryGif,
    galleryImages,
    onSetGalleryImages,
    onSetGalleryGif
  } = useGalleryGif(gif);

  const currentUser = useVisitorInfo();
  const eventSource = useAnalyticsStore((state) => state.eventSource);

  const { liked, handleLike } = useLikeActions({
    gifId: gif.id,
    source: eventSource?.name ?? EventSource.Other,
    sourceId: eventSource?.id ?? null,
    initialLikesCount: gif.likes,
    ref: wrapRef,
    boosted: !!gif.cta,
    position,
    playCount
  });

  const { sound, toggleSound } = usePlayerSound({ ref: wrapRef });

  const onMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>, gif: IGif) => {
    onOpenMoreMenu({
      gif,
      anchorEl: event.currentTarget || null,
      position,
      playCount,
      hideGif: hideGif
    });
    metricsBus.emit("optionClicked");
  }, [hideGif, eventSource, position, playCount]);

  useEffect(() => {
    if (isVisible) {
      mainBus.emit("set_playing_gif", { id: gif.id });
    }
  }, [gif.id, isVisible]);

  return (
    <Fragment>
      <div className="GifPreviewV2-BackdropWrap">
        <img
          // @ts-expect-error fetchpriority is known attribute
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority={position === 1 ? "high" : undefined}
          src={gif.urls.thumbnail}
          alt={`Poster for ${gif.id}`}
        />
      </div>

      {gif.type === GifType.Video &&
        (
          <Suspense>
            <Player
              poster={gif.urls.thumbnail}
              position={position}
              gif={gif}
              sound={sound}
              handleLike={handleLike}
              isVisible={isVisible}
              isHLS={!!gif.hls}
              onSound={toggleSound}
              currentUserName={currentUser?.username}
              sendGifView={sendGifView}
              playCount={playCount}
              incrementPlayCount={incrementPlayCount}
            />
          </Suspense>
        )
      }

      {gif.type === GifType.Image && (
        gif.gallery ? (
          <Suspense>
            <GalleryGif
              galleryId={gif.gallery}
              initialGif={gif}
              onSetGalleryGif={onSetGalleryGif}
              onSetGalleryImages={onSetGalleryImages}
              onLike={handleLike}
              isVisible={isVisible}
              sendGifView={sendGifView}
            />
          </Suspense>
        ) : (
          <ImageGif
            onLike={handleLike}
            gif={gif}
            isVisible={isVisible}
            sendGifView={sendGifView}
          />
        )
      )}

      <div className="GifPreviewV2-InfoAndSidebar">
        <div className="GifPreviewV2-SideBarWrap">
          <Suspense>
            <Sidebar
              onMenu={onMenu}
              onLike={handleLike}
              onSound={toggleSound}
              gifSound={sound}
              gif={currentGalleryGif !== null && gif.id === gif.aliasId ? currentGalleryGif : gif}
              liked={liked}
              likesCount={gif.likes}
            />
          </Suspense>
        </div>
        <div className="GifPreviewV2-MetaInfo" onContextMenu={event => event.preventDefault()}>
          <Suspense>
            <MetaInfo
              gif={gif}
              user={props.user}
              currentUser={currentUser}
              position={position}
              isVisible={isVisible}
              galleryImagesQty={galleryImages}
              playCount={playCount}
            />
          </Suspense>
        </div>
      </div>

      <LikeSoundOverlay />
    </Fragment>
  );
};
